import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useApi } from "@/lib/api";
import { Filters } from "@/components/Filters";
import { ListingCard } from "@/components/ListingCard";

const KEYS = ["q", "brand", "fuel", "gearbox", "seller_type", "mode", "price_min", "price_max", "year_min", "year_max", "km_max", "city", "sort"];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const f = useMemo(() => Object.fromEntries(KEYS.map((k) => [k, params.get(k) || ""])), [params]);
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const api = useApi();

  useEffect(() => { api("get", "/listings/brands").then(setBrands); }, [api]);
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      const p = new URLSearchParams(Object.entries(f).filter(([, v]) => v));
      api("get", `/listings?${p}`).then(setItems).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [f, api]);

  const set = (k, v) => {
    const p = new URLSearchParams(params);
    v ? p.set(k, v) : p.delete(k);
    setParams(p, { replace: true });
  };
  const reset = () => setParams({}, { replace: true });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Cerca veicoli</h1>
          <p className="text-slate-500 mt-1" data-testid="search-results-count">{loading ? "Caricamento..." : `${items.length} risultati`}</p>
        </div>
        <div className="flex flex-wrap sm:flex-nowrap gap-2">
          <div className="relative w-full sm:w-auto sm:flex-1 md:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="search-query-input" value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Marca o modello" className="pl-9 bg-white" />
          </div>
          <Select value={f.sort || "recenti"} onValueChange={(v) => set("sort", v)}>
            <SelectTrigger data-testid="search-sort-select" className="flex-1 sm:flex-none sm:w-44 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recenti">Più recenti</SelectItem>
              <SelectItem value="prezzo_asc">Prezzo crescente</SelectItem>
              <SelectItem value="prezzo_desc">Prezzo decrescente</SelectItem>
              <SelectItem value="anno_desc">Anno più recente</SelectItem>
              <SelectItem value="km_asc">Meno chilometri</SelectItem>
            </SelectContent>
          </Select>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="lg:hidden" data-testid="search-open-filters-button"><SlidersHorizontal size={16} /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="overflow-y-auto">
              <SheetHeader><SheetTitle>Filtri</SheetTitle></SheetHeader>
              <div className="mt-6"><Filters f={f} set={set} brands={brands} onReset={reset} /></div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <div className="grid lg:grid-cols-[260px_1fr] gap-8">
        <aside className="hidden lg:block sticky top-24 self-start bg-white border border-slate-200 rounded-2xl p-5">
          <Filters f={f} set={set} brands={brands} onReset={reset} />
        </aside>
        <div>
          {!loading && items.length === 0 ? (
            <div className="text-center py-24 border border-dashed border-slate-300 rounded-2xl" data-testid="search-empty-state">
              <p className="font-heading text-xl font-semibold">Nessun veicolo trovato</p>
              <p className="text-slate-500 mt-1">Prova a modificare i filtri.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="search-results-grid">
              {items.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
